/**
 * Belo Skio Integration
 * 
 * This JavaScript file handles the integration with Skio for subscription management.
 * It includes functions to create subscriptions, manage them, and handle webhooks.
 * 
 * @copyright Belo 2025
 * @license Proprietary - Unauthorized copying of this file is strictly prohibited
 */

// Only define SkioIntegration if it doesn't already exist
if (typeof window.SkioIntegration === 'undefined') {
  class SkioIntegration {
    constructor(config = {}) {
      this.apiUrl = config.apiUrl || 'https://graphql.skio.com/v1/graphql';
      this.shopifyDomain = config.shopifyDomain || 'ever-boost.shopify.com';
      this.apiToken = config.apiToken || 'a6723e6f-1a5d-4dc9-98f6-b14548cdf025';
      this.debug = config.debug || false;
      
      // Selling plan IDs for different subscription frequencies
      // These should be fetched from Skio in a real implementation
      this.sellingPlanIds = {
        4: 690478285093, // 4-week frequency plan ID
        6: 690478481701  // 6-week frequency plan ID
      };
      
      // Initialize the integration
      this.init();
    }
    
    /**
     * Initialize the Skio integration
     */
    init() {
      // Listen for cart form submissions to intercept and handle subscription items
      document.addEventListener('DOMContentLoaded', () => {
        this.setupCartIntegration();
      });
    }
    
    /**
     * Set up cart integration to handle subscription items
     */
    setupCartIntegration() {
      // Override the addItemsToCart function if it exists
      if (window.addItemsToCart) {
        const originalAddItemsToCart = window.addItemsToCart;
        
        window.addItemsToCart = (items) => {
          // Check if any items are subscription items
          const hasSubscriptionItems = items.some(item => 
            item.properties && 
            item.properties.shipping_interval_frequency
          );
          
          if (hasSubscriptionItems) {
            // Process subscription items
            this.processSubscriptionItems(items);
          } else {
            // Use original function for one-time purchases
            originalAddItemsToCart(items);
          }
        };
      }
    }
    
    /**
     * Process subscription items before adding to cart
     * @param {Array} items - The items to add to the cart
     */
    processSubscriptionItems(items) {
      try {
        // Get the first item to determine frequency
        const firstItem = items.find(item => 
          item.properties && 
          item.properties.shipping_interval_frequency
        );
        
        if (!firstItem) {
          throw new Error('No subscription items found');
        }
        
        const frequency = parseInt(firstItem.properties.shipping_interval_frequency);
        const sellingPlanId = this.sellingPlanIds[frequency];
        
        if (!sellingPlanId) {
          throw new Error(`No selling plan found for frequency: ${frequency}`);
        }
        
        // Prepare data for Shopify AJAX API
        const data = {
          items: items.map(item => {
            // Create a new item with selling_plan_id
            // Make sure we're using the correct format for Shopify cart API
            return {
              id: item.id,
              quantity: item.quantity,
              selling_plan: sellingPlanId,
              properties: item.properties || {}
            };
          })
        };
        
        // Show loading state
        const addToCartButton = document.querySelector('.add-to-cart-button');
        if (addToCartButton) {
          addToCartButton.textContent = 'Adding...';
          addToCartButton.disabled = true;
        }
        
        // Send request to add items to cart
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        })
        .then(async response => {
          if (!response.ok) {
            // Try to get the error details from the response
            let errorMessage = `HTTP error! Status: ${response.status}`;
            try {
              const errorData = await response.json();
              
              if (errorData && errorData.description) {
                errorMessage += ` - ${errorData.description}`;
              } else if (errorData && errorData.message) {
                errorMessage += ` - ${errorData.message}`;
              }
            } catch (e) {
              // Could not parse error response
            }
            
            throw new Error(errorMessage);
          }
          return response.json();
        })
        .then(data => {
          // Success - redirect directly to checkout instead of cart
          window.location.href = '/checkout';
        })
        .catch(error => {
          if (addToCartButton) {
            addToCartButton.textContent = 'Error - Try Again';
            addToCartButton.disabled = false;
          }
          
          // Show error details to help with debugging
          alert('Error adding subscription to cart: ' + error.message);
          
          // Fall back to regular cart handling
          if (window.sendCartRequest) {
            window.sendCartRequest(data);
          }
        });
      } catch (error) {
        alert('Error processing subscription: ' + error.message);
        
        // Fall back to regular cart handling
        if (window.sendCartRequest) {
          const data = {
            items: items.map(item => ({
              id: item.id,
              quantity: item.quantity,
              properties: item.properties || {}
            }))
          };
          window.sendCartRequest(data);
        }
      }
    }

    /**
     * Set the API token
     * @param {string} token - The API token
     */
    setApiToken(token) {
      this.apiToken = token;
    }

    /**
     * Make a GraphQL request to the Skio API
     * @param {string} query - The GraphQL query
     * @param {Object} variables - The variables for the query
     * @returns {Promise<Object>} - The response from the API
     */
    async request(query, variables = {}) {
      try {
        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': `API ${this.apiToken}`
          },
          body: JSON.stringify({
            query,
            variables
          })
        });

        const data = await response.json();

        if (data.errors) {
          throw new Error(data.errors[0].message);
        }

        return data.data;
      } catch (error) {
        throw error;
      }
    }

    /**
     * Get a subscription by ID
     * @param {string} id - The subscription ID
     * @returns {Promise<Object>} - The subscription data
     */
    async getSubscription(id) {
      const query = `
        query SubscriptionByPk($id: uuid!) {
          SubscriptionByPk(id: $id) {
            id
            platformId
            status
            nextBillingDate
            cyclesCompleted
            currencyCode
            deliveryPrice
            billingPolicyId
            deliveryPolicyId
            StorefrontUser {
              id
              email
              firstName
              lastName
            }
            SubscriptionLines(where: {removedAt: {_is_null: true}}) {
              id
              quantity
              priceWithoutDiscount
              ProductVariant {
                id
                title
                price
                Product {
                  id
                  title
                }
              }
            }
          }
        }
      `;

      const variables = { id };
      const data = await this.request(query, variables);
      return data.SubscriptionByPk;
    }

    /**
     * Get all subscriptions for a customer
     * @param {string} email - The customer's email
     * @returns {Promise<Array>} - The customer's subscriptions
     */
    async getCustomerSubscriptions(email) {
      const query = `
        query getSubscriptions($email: String!) {
          Subscriptions(
            where: {
              StorefrontUser: {email: {_eq: $email}}
            }, 
            order_by: {createdAt: desc}
          ) {
            id
            platformId
            status
            nextBillingDate
            cyclesCompleted
            currencyCode
            deliveryPrice
            SubscriptionLines(where: {removedAt: {_is_null: true}}) {
              priceWithoutDiscount
              quantity
              ProductVariant {
                title
                Product {
                  title
                }
              }
            }
          }
        }
      `;

      const variables = { email };
      const data = await this.request(query, variables);
      return data.Subscriptions;
    }

    /**
     * Create a new subscription
     * @param {Object} input - The subscription input data
     * @returns {Promise<Object>} - The created subscription
     */
    async createSubscription(input) {
      const mutation = `
        mutation createSubscription($input: CreateSubscriptionInput!) {
          createSubscription(input: $input) {
            id
            platformId
          }
        }
      `;

      const variables = { input };
      const data = await this.request(mutation, variables);
      return data.createSubscription;
    }

    /**
     * Cancel a subscription
     * @param {string} id - The subscription ID
     * @param {string} reason - The reason for cancellation
     * @returns {Promise<Object>} - The result of the cancellation
     */
    async cancelSubscription(id, reason = '') {
      const mutation = `
        mutation cancelSubscription($input: CancelSubscriptionInput!) {
          cancelSubscription(input: $input) {
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId: id,
          reason
        }
      };

      const data = await this.request(mutation, variables);
      return data.cancelSubscription;
    }

    /**
     * Pause a subscription
     * @param {string} id - The subscription ID
     * @returns {Promise<Object>} - The result of the pause
     */
    async pauseSubscription(id) {
      const mutation = `
        mutation pauseSubscription($input: pauseSubscriptionInput!) {
          pauseSubscription(input: $input) {
            message
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId: id
        }
      };

      const data = await this.request(mutation, variables);
      return data.pauseSubscription;
    }

    /**
     * Unpause a subscription
     * @param {string} id - The subscription ID
     * @returns {Promise<Object>} - The result of the unpause
     */
    async unpauseSubscription(id) {
      const mutation = `
        mutation unpauseSubscription($input: unpauseSubscriptionInput!) {
          unpauseSubscription(input: $input) {
            message
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId: id
        }
      };

      const data = await this.request(mutation, variables);
      return data.unpauseSubscription;
    }

    /**
     * Update the next billing date for a subscription
     * @param {string} id - The subscription ID
     * @param {string} date - The new billing date (ISO format)
     * @returns {Promise<Object>} - The result of the update
     */
    async updateNextBillingDate(id, date) {
      const mutation = `
        mutation updateNextBillingDate($input: UpdateNextBillingDateInput!) {
          updateNextBillingDate(input: $input) {
            message
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId: id,
          nextBillingDate: date
        }
      };

      const data = await this.request(mutation, variables);
      return data.updateNextBillingDate;
    }

    /**
     * Update the delivery frequency for a subscription
     * @param {string} id - The subscription ID
     * @param {string} interval - The interval type (DAY, WEEK, MONTH)
     * @param {number} intervalCount - The interval count
     * @returns {Promise<Object>} - The result of the update
     */
    async updateDeliveryFrequency(id, interval, intervalCount) {
      const mutation = `
        mutation subscriptionEditInterval($input: SubscriptionEditIntervalInput!) {
          subscriptionEditInterval(input: $input) {
            subscriptionId
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId: id,
          interval,
          intervalCount
        }
      };

      const data = await this.request(mutation, variables);
      return data.subscriptionEditInterval;
    }

    /**
     * Update the quantity of a subscription line
     * @param {string} lineId - The subscription line ID
     * @param {number} quantity - The new quantity
     * @returns {Promise<Object>} - The result of the update
     */
    async updateSubscriptionLineQuantity(lineId, quantity) {
      const mutation = `
        mutation updateSubscriptionLine($input: UpdateSubscriptionLineInput!) {
          updateSubscriptionLine(input: $input) {
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionLineId: lineId,
          quantity
        }
      };

      const data = await this.request(mutation, variables);
      return data.updateSubscriptionLine;
    }

    /**
     * Add a product to a subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {string} productVariantId - The product variant ID
     * @param {number} quantity - The quantity
     * @param {number} price - The price (optional)
     * @returns {Promise<Object>} - The result of the addition
     */
    async addProductToSubscription(subscriptionId, productVariantId, quantity, price = null) {
      const mutation = `
        mutation addSubscriptionLine($input: AddSubscriptionLineInput!) {
          addSubscriptionLine(input: $input) {
            ok
          }
        }
      `;

      const input = {
        subscriptionId,
        productVariantId,
        quantity
      };

      if (price !== null) {
        input.price = price;
      }

      const variables = { input };
      const data = await this.request(mutation, variables);
      return data.addSubscriptionLine;
    }

    /**
     * Swap a product variant in a subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {string} oldVariantId - The old product variant ID
     * @param {string} newVariantId - The new product variant ID
     * @returns {Promise<Object>} - The result of the swap
     */
    async swapProductVariant(subscriptionId, oldVariantId, newVariantId) {
      const mutation = `
        mutation swapSubscriptionProductVariants($input: SwapSubscriptionProductVariantsInput!) {
          swapSubscriptionProductVariants(input: $input) {
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId,
          oldVariantId,
          newVariantId
        }
      };

      const data = await this.request(mutation, variables);
      return data.swapSubscriptionProductVariants;
    }

    /**
     * Update a dynamic box subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {Array} items - The items in the dynamic box
     * @returns {Promise<Object>} - The result of the update
     */
    async updateDynamicBoxSubscription(subscriptionId, items) {
      const mutation = `
        mutation updateDynamicBoxSubscription($input: UpdateDynamicBoxSubscriptionInput!) {
          updateDynamicBoxSubscription(input: $input) {
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId,
          items
        }
      };

      const data = await this.request(mutation, variables);
      return data.updateDynamicBoxSubscription;
    }

    /**
     * Apply a discount code to a subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {string} discountCode - The discount code
     * @returns {Promise<Object>} - The result of applying the discount
     */
    async applyDiscountCode(subscriptionId, discountCode) {
      const mutation = `
        mutation applyDiscountCode($input: ApplyDiscountCodeInput!) {
          applyDiscountCode(input: $input) {
            message
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId,
          discountCode
        }
      };

      const data = await this.request(mutation, variables);
      return data.applyDiscountCode;
    }

    /**
     * Update the shipping address for a subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {Object} address - The new shipping address
     * @returns {Promise<Object>} - The result of the update
     */
    async updateShippingAddress(subscriptionId, address) {
      const mutation = `
        mutation updateSubscriptionShippingAddress($input: UpdateSubscriptionShippingAddressInput!) {
          updateSubscriptionShippingAddress(input: $input) {
            message
            ok
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId,
          ...address
        }
      };

      const data = await this.request(mutation, variables);
      return data.updateSubscriptionShippingAddress;
    }

    /**
     * Update the payment method for a subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {string} paymentMethodId - The payment method ID
     * @returns {Promise<Object>} - The result of the update
     */
    async updatePaymentMethod(subscriptionId, paymentMethodId) {
      const mutation = `
        mutation subscriptionUpdatePaymentMethod($input: SubscriptionUpdatePaymentMethodInput!) {
          subscriptionUpdatePaymentMethod(input: $input) {
            subscriptionId
          }
        }
      `;

      const variables = {
        input: {
          subscriptionId,
          paymentMethodId
        }
      };

      const data = await this.request(mutation, variables);
      return data.subscriptionUpdatePaymentMethod;
    }

    /**
     * Create a subscription from a cart
     * @param {Object} cart - The cart data
     * @returns {Object} - The formatted subscription input
     */
    formatSubscriptionInput(cart) {
      // This is a helper function to format the cart data into the expected input format for createSubscription
      const input = {
        storefrontUserId: cart.customer.id,
        billingPolicy: {
          interval: cart.sellingPlan.billingPolicy.interval,
          intervalCount: cart.sellingPlan.billingPolicy.intervalCount
        },
        deliveryPolicy: {
          interval: cart.sellingPlan.deliveryPolicy.interval,
          intervalCount: cart.sellingPlan.deliveryPolicy.intervalCount
        },
        lines: cart.items.map(item => ({
          productVariantId: item.variantId,
          quantity: item.quantity,
          price: item.price
        })),
        shippingAddress: cart.shippingAddress,
        paymentMethodId: cart.paymentMethodId
      };

      return input;
    }
  }

  // Export the SkioIntegration class
  window.SkioIntegration = SkioIntegration;
}

// Only define DynamicBoxIntegration if it doesn't already exist
if (typeof window.DynamicBoxIntegration === 'undefined') {
  /**
   * Dynamic Box Integration
   * 
   * This section handles the integration with Skio's Dynamic Box feature.
   * It includes functions to create and manage dynamic boxes.
   */
  class DynamicBoxIntegration {
    constructor(skioIntegration) {
      this.skio = skioIntegration;
    }

    /**
     * Get a dynamic box by ID
     * @param {string} id - The dynamic box ID
     * @returns {Promise<Object>} - The dynamic box data
     */
    async getDynamicBox(id) {
      const query = `
        query DynamicBoxByPk($id: uuid!) {
          DynamicBoxByPk(id: $id) {
            id
            name
            description
            minItems
            maxItems
            Product {
              id
              title
            }
            DynamicBox_SelectableProductVariants {
              id
              productVariantId
              ProductVariant {
                id
                title
                price
                Product {
                  id
                  title
                }
              }
            }
          }
        }
      `;

      const variables = { id };
      const data = await this.skio.request(query, variables);
      return data.DynamicBoxByPk;
    }

    /**
     * Create a dynamic box subscription
     * @param {Object} input - The subscription input data
     * @returns {Promise<Object>} - The created subscription
     */
    async createDynamicBoxSubscription(input) {
      // This is similar to createSubscription but with dynamic box specific formatting
      return await this.skio.createSubscription(input);
    }

    /**
     * Update a dynamic box subscription
     * @param {string} subscriptionId - The subscription ID
     * @param {Array} items - The items in the dynamic box
     * @returns {Promise<Object>} - The result of the update
     */
    async updateDynamicBox(subscriptionId, items) {
      return await this.skio.updateDynamicBoxSubscription(subscriptionId, items);
    }

    /**
     * Format dynamic box items for the API
     * @param {Array} selectedItems - The selected items
     * @returns {Array} - The formatted items
     */
    formatDynamicBoxItems(selectedItems) {
      return selectedItems.map(item => ({
        productVariantId: item.variantId,
        quantity: item.quantity
      }));
    }
  }

  // Export the DynamicBoxIntegration class
  window.DynamicBoxIntegration = DynamicBoxIntegration;
}
